import React, { useState, forwardRef } from "react";
import DatePicker from "react-datepicker";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subDays,
  subMonths
} from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./Calender.module.css";
import "./calender.css";
import { Image } from "react-bootstrap";
import { ASSESTS } from "../../../constant/assest";
import CustomButton from "../CustomButton/CustomButton";
import cn from 'classnames';
import { flip, shift, offset } from "@floating-ui/dom";

export enum CalenderBtnTheme {
  DEFAULT = 'typeDefault',
  LIGHT = 'typeLightColor',
}

interface CalenderProps {
    className?: string;
    calenderBtnTheme?: CalenderBtnTheme;
    onDateChange: (range: [Date | null, Date | null], filter: string | null) => void;
    dateRange: [Date | null, Date | null];
    activeFilter: string | null;
}

const Calender: React.FC<CalenderProps> = ({
    className,
    calenderBtnTheme = CalenderBtnTheme.DEFAULT,
    onDateChange,
    dateRange,
    activeFilter,
}) => {
  const [startDate, endDate] = dateRange;
  const [openToDate, setOpenToDate] = useState<Date | null>(new Date());

  const CustomInput = forwardRef<
    HTMLButtonElement,
    { value?: string; onClick?: () => void }
  >(({ value, onClick }, ref) => (
    <CustomButton
      className={cn(styles.dateBtn, styles[calenderBtnTheme || ''])} 
      onClick={onClick}
      ref={ref as React.Ref<HTMLButtonElement>}
    >
      {value && startDate && endDate
        ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
        : "Filter by Date Range"}
      <Image src={ASSESTS.SELECTDOWN} width={20} height={20} alt="Date" />
    </CustomButton>
  ));

  const renderBtn = (className: string, onClick: () => void, text: string) => {
    return (
      <button
          className={className}
          onClick={onClick}
        >
          {text}
      </button>
    );
  };

  const handleQuickSelect = (type: string) => {
    const today = new Date();

    switch (type) {
      case "today": {
        setOpenToDate(today);
        onDateChange([today, today], "today");
        break;
      }
      case "yesterday": {
        const yest = subDays(today, 1);
        setOpenToDate(yest);
        onDateChange([yest, yest], "yesterday");
        break;
      }
      case "lastWeek": {
        const start = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        const end = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        setOpenToDate(start);
        onDateChange([start, end], "lastWeek");
        break;
      }
      case "lastMonth": {
        const lastMonth = subMonths(today, 1);
        const start = startOfMonth(lastMonth);
        const end = endOfMonth(lastMonth);
        setOpenToDate(start);
        onDateChange([start, end], "lastMonth");
        break;
      }
      case "lastYear": {
        const lastYear = subMonths(today, 12);
        const start = startOfYear(lastYear);
        const end = endOfYear(lastYear);
        setOpenToDate(start);
        onDateChange([start, end], "lastYear");
        break;
      }
      case "currentYear": {
        const start = startOfYear(today);
        const end = endOfYear(today);
        setOpenToDate(start);
        onDateChange([start, end], "currentYear");
        break;
      }
      case "reset": {
        const start = startOfYear(today);
        const end = endOfYear(today);
        setOpenToDate(start);
        onDateChange([start, end], "currentYear");
        break;
      }
    }
  };

  return (
    <div className={cn(className, "customDateRange")}>
      <DatePicker
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={(update: [Date | null, Date | null]) => {
          if (!update[0] && !update[1]) {
            onDateChange(update, null);
          } else {
            onDateChange(update, 'custom');
          }
        }}
        customInput={<CustomInput />}
        openToDate={openToDate ?? new Date()}
        popperPlacement="bottom-end"
        popperClassName="calendar-popper"
        popperModifiers={[
          flip({ fallbackPlacements: ["bottom-start", "top-end", "top-start"] }),
          shift({ padding: 8 }),
          offset(4),
        ]}
        renderCustomHeader={({ monthDate, decreaseMonth, increaseMonth }) => (
          <div className={'customHeader'}>
            <div className={'currentMonth'}>
                <div className={'currentMonthText'}>
                    {monthDate.toLocaleString("default", {
                        month: "long",
                        year: "numeric"
                    })}
                </div>
            </div>
            <div className="arrowBtns">
                <button onClick={decreaseMonth} className={'navBtn'}>
                    <Image src={ASSESTS.LEFTARROW} width={36} height={36} alt="Prev" />
                </button>
                <button onClick={increaseMonth} className={'navBtn'}>
                    <Image src={ASSESTS.RIGHTARROW} width={36} height={36} alt="Next" />
                </button>
            </div>
          </div>
        )}
        renderDayContents={(day: number) => (
            <div className="dateText">{day}</div>
        )}
      >
        <div className="quick-menu">
          {renderBtn(activeFilter === "today" ? "active" : "" , () => handleQuickSelect("today"), 'Today')}
          {renderBtn(activeFilter === "yesterday" ? "active" : "" , () => handleQuickSelect("yesterday"), 'Yesterday')}
          {renderBtn(activeFilter === "lastWeek" ? "active" : "" , () => handleQuickSelect("lastWeek"), 'Last week')}
          {renderBtn(activeFilter === "lastMonth" ? "active" : "" , () => handleQuickSelect("lastMonth"), 'Last month')}
          {renderBtn(activeFilter === "lastYear" ? "active" : "" , () => handleQuickSelect("lastYear"), 'Last year')}
          {renderBtn(activeFilter === "currentYear" ? "active" : "" , () => handleQuickSelect("currentYear"), 'Current year')}
        </div>
        <div className="resetAll">
            {renderBtn('reset' , () => handleQuickSelect("reset"), 'Reset')}
        </div>
      </DatePicker>
    </div>
  );
};

export default React.memo(Calender);